// D&D 5e Monster Maker Data
// Extracted and adapted from https://github.com/ebshimizu/5e-monster-maker (MIT License)

// Dice types
export const DICE = {
  d1: 1,
  d4: 4,
  d6: 6,
  d8: 8,
  d10: 10,
  d12: 12,
  d20: 20,
}

export const DICE_SELECT = Object.entries(DICE).map(([label, value]) => ({ label, value }))

export const DIE_LOOKUP = {}
Object.entries(DICE).forEach(([k, v]) => {
  DIE_LOOKUP[v] = k
})

// Creature sizes
export const CREATURE_SIZE = [
  'Tiny',
  'Small',
  'Medium',
  'Large',
  'Huge',
  'Gargantuan',
]

export const HD_FOR_SIZE = {
  Tiny: DICE.d4,
  Small: DICE.d6,
  Medium: DICE.d8,
  Large: DICE.d10,
  Huge: DICE.d12,
  Gargantuan: DICE.d20,
}

// Creature types
export const CREATURE_TYPE = [
  'Aberration',
  'Beast',
  'Celestial',
  'Construct',
  'Dragon',
  'Elemental',
  'Fey',
  'Fiend',
  'Giant',
  'Humanoid',
  'Monstrosity',
  'Ooze',
  'Plant',
  'Undead',
]

// Alignments
export const ALIGNMENT = [
  'Unaligned',
  'Lawful Good',
  'Neutral Good',
  'Chaotic Good',
  'Lawful Neutral',
  'Neutral',
  'Chaotic Neutral',
  'Lawful Evil',
  'Neutral Evil',
  'Chaotic Evil',
]

// Movement types
export const MOVEMENT_TYPES = ['walk', 'fly', 'burrow', 'climb', 'swim']

// Ability scores
export const STATS = ['STR', 'DEX', 'CON', 'INT', 'WIS', 'CHA']

export const STAT_NAMES = {
  STR: 'Strength',
  DEX: 'Dexterity',
  CON: 'Constitution',
  INT: 'Intelligence',
  WIS: 'Wisdom',
  CHA: 'Charisma',
}

// Skills with their associated ability scores
export const SKILLS = {
  ACROBATICS: { stat: 'DEX', label: 'Acrobatics' },
  ANIMAL_HANDLING: { stat: 'WIS', label: 'Animal Handling' },
  ARCANA: { stat: 'INT', label: 'Arcana' },
  ATHLETICS: { stat: 'STR', label: 'Athletics' },
  DECEPTION: { stat: 'CHA', label: 'Deception' },
  HISTORY: { stat: 'INT', label: 'History' },
  INSIGHT: { stat: 'WIS', label: 'Insight' },
  INTIMIDATION: { stat: 'CHA', label: 'Intimidation' },
  INVESTIGATION: { stat: 'INT', label: 'Investigation' },
  MEDICINE: { stat: 'WIS', label: 'Medicine' },
  NATURE: { stat: 'INT', label: 'Nature' },
  PERCEPTION: { stat: 'WIS', label: 'Perception' },
  PERFORMANCE: { stat: 'CHA', label: 'Performance' },
  PERSUASION: { stat: 'CHA', label: 'Persuasion' },
  RELIGION: { stat: 'INT', label: 'Religion' },
  SLEIGHT_OF_HAND: { stat: 'DEX', label: 'Sleight of Hand' },
  STEALTH: { stat: 'DEX', label: 'Stealth' },
  SURVIVAL: { stat: 'WIS', label: 'Survival' },
}

export const SKILL_LIST = Object.values(SKILLS).map(s => s.label)

// Damage types
export const DAMAGE_TYPES = [
  'Acid',
  'Bludgeoning',
  'Cold',
  'Fire',
  'Force',
  'Lightning',
  'Necrotic',
  'Piercing',
  'Poison',
  'Psychic',
  'Radiant',
  'Slashing',
  'Thunder',
]

// Conditions
export const CONDITIONS = [
  'Blinded',
  'Charmed',
  'Deafened',
  'Exhaustion',
  'Frightened',
  'Grappled',
  'Incapacitated',
  'Invisible',
  'Paralyzed',
  'Petrified',
  'Poisoned',
  'Prone',
  'Restrained',
  'Stunned',
  'Unconscious',
]

// Senses
export const SENSES = ['blindsight', 'darkvision', 'tremorsense', 'truesight']

// Attack types
export const ATTACK_TYPES = ['Melee', 'Ranged', 'Melee or Ranged', 'Reach']

// Action types (for recharge)
export const RECHARGE_TIMES = [
  { label: 'None', value: '' },
  { label: 'Recharge 6', value: 'RECHARGE_6' },
  { label: 'Recharge 5-6', value: 'RECHARGE_5_6' },
  { label: 'Recharge 4-6', value: 'RECHARGE_4_6' },
  { label: 'Short Rest', value: 'SHORT_REST' },
  { label: 'Long Rest', value: 'LONG_REST' },
  { label: '1/Day', value: '1_DAY' },
  { label: '2/Day', value: '2_DAY' },
  { label: '3/Day', value: '3_DAY' },
]

// Challenge Rating data
export const CR_DATA = [
  { cr: '0', proficiency: 2, ac: 13, hpMin: 1, hpMax: 6, attack: 3, dprMin: 0, dprMax: 1, saveDc: 13, numeric: 0, xp: 10 },
  { cr: '1/8', proficiency: 2, ac: 13, hpMin: 7, hpMax: 35, attack: 3, dprMin: 2, dprMax: 3, saveDc: 13, numeric: 0.125, xp: 25 },
  { cr: '1/4', proficiency: 2, ac: 13, hpMin: 36, hpMax: 49, attack: 3, dprMin: 4, dprMax: 5, saveDc: 13, numeric: 0.25, xp: 50 },
  { cr: '1/2', proficiency: 2, ac: 13, hpMin: 50, hpMax: 70, attack: 3, dprMin: 6, dprMax: 8, saveDc: 13, numeric: 0.5, xp: 100 },
  { cr: '1', proficiency: 2, ac: 13, hpMin: 71, hpMax: 85, attack: 3, dprMin: 9, dprMax: 14, saveDc: 13, numeric: 1, xp: 200 },
  { cr: '2', proficiency: 2, ac: 13, hpMin: 86, hpMax: 100, attack: 3, dprMin: 15, dprMax: 20, saveDc: 13, numeric: 2, xp: 450 },
  { cr: '3', proficiency: 2, ac: 13, hpMin: 101, hpMax: 115, attack: 4, dprMin: 21, dprMax: 26, saveDc: 13, numeric: 3, xp: 700 },
  { cr: '4', proficiency: 2, ac: 14, hpMin: 116, hpMax: 130, attack: 5, dprMin: 27, dprMax: 32, saveDc: 14, numeric: 4, xp: 1100 },
  { cr: '5', proficiency: 3, ac: 15, hpMin: 131, hpMax: 145, attack: 6, dprMin: 33, dprMax: 38, saveDc: 15, numeric: 5, xp: 1800 },
  { cr: '6', proficiency: 3, ac: 15, hpMin: 146, hpMax: 160, attack: 6, dprMin: 39, dprMax: 44, saveDc: 15, numeric: 6, xp: 2300 },
  { cr: '7', proficiency: 3, ac: 15, hpMin: 161, hpMax: 175, attack: 6, dprMin: 45, dprMax: 50, saveDc: 15, numeric: 7, xp: 2900 },
  { cr: '8', proficiency: 3, ac: 16, hpMin: 176, hpMax: 190, attack: 7, dprMin: 51, dprMax: 56, saveDc: 16, numeric: 8, xp: 3900 },
  { cr: '9', proficiency: 4, ac: 16, hpMin: 191, hpMax: 205, attack: 7, dprMin: 57, dprMax: 62, saveDc: 16, numeric: 9, xp: 5000 },
  { cr: '10', proficiency: 4, ac: 17, hpMin: 206, hpMax: 220, attack: 7, dprMin: 63, dprMax: 68, saveDc: 16, numeric: 10, xp: 5900 },
  { cr: '11', proficiency: 4, ac: 17, hpMin: 221, hpMax: 235, attack: 8, dprMin: 69, dprMax: 74, saveDc: 17, numeric: 11, xp: 7200 },
  { cr: '12', proficiency: 4, ac: 17, hpMin: 236, hpMax: 250, attack: 8, dprMin: 75, dprMax: 80, saveDc: 17, numeric: 12, xp: 8400 },
  { cr: '13', proficiency: 5, ac: 18, hpMin: 251, hpMax: 265, attack: 8, dprMin: 81, dprMax: 86, saveDc: 18, numeric: 13, xp: 10000 },
  { cr: '14', proficiency: 5, ac: 18, hpMin: 266, hpMax: 280, attack: 8, dprMin: 87, dprMax: 92, saveDc: 18, numeric: 14, xp: 11500 },
  { cr: '15', proficiency: 5, ac: 18, hpMin: 281, hpMax: 295, attack: 8, dprMin: 93, dprMax: 98, saveDc: 18, numeric: 15, xp: 13000 },
  { cr: '16', proficiency: 5, ac: 18, hpMin: 296, hpMax: 310, attack: 9, dprMin: 99, dprMax: 104, saveDc: 18, numeric: 16, xp: 15000 },
  { cr: '17', proficiency: 6, ac: 19, hpMin: 311, hpMax: 325, attack: 10, dprMin: 105, dprMax: 110, saveDc: 19, numeric: 17, xp: 18000 },
  { cr: '18', proficiency: 6, ac: 19, hpMin: 326, hpMax: 340, attack: 10, dprMin: 111, dprMax: 116, saveDc: 19, numeric: 18, xp: 20000 },
  { cr: '19', proficiency: 6, ac: 19, hpMin: 341, hpMax: 355, attack: 10, dprMin: 117, dprMax: 122, saveDc: 19, numeric: 19, xp: 22000 },
  { cr: '20', proficiency: 6, ac: 19, hpMin: 356, hpMax: 400, attack: 10, dprMin: 123, dprMax: 140, saveDc: 19, numeric: 20, xp: 25000 },
  { cr: '21', proficiency: 7, ac: 19, hpMin: 401, hpMax: 445, attack: 11, dprMin: 141, dprMax: 158, saveDc: 20, numeric: 21, xp: 33000 },
  { cr: '22', proficiency: 7, ac: 19, hpMin: 446, hpMax: 490, attack: 11, dprMin: 159, dprMax: 176, saveDc: 20, numeric: 22, xp: 41000 },
  { cr: '23', proficiency: 7, ac: 19, hpMin: 491, hpMax: 535, attack: 11, dprMin: 177, dprMax: 194, saveDc: 20, numeric: 23, xp: 50000 },
  { cr: '24', proficiency: 7, ac: 19, hpMin: 536, hpMax: 580, attack: 12, dprMin: 195, dprMax: 212, saveDc: 21, numeric: 24, xp: 62000 },
  { cr: '25', proficiency: 8, ac: 19, hpMin: 581, hpMax: 625, attack: 12, dprMin: 213, dprMax: 230, saveDc: 21, numeric: 25, xp: 75000 },
  { cr: '26', proficiency: 8, ac: 19, hpMin: 626, hpMax: 670, attack: 12, dprMin: 231, dprMax: 248, saveDc: 21, numeric: 26, xp: 90000 },
  { cr: '27', proficiency: 8, ac: 19, hpMin: 671, hpMax: 715, attack: 13, dprMin: 249, dprMax: 266, saveDc: 22, numeric: 27, xp: 105000 },
  { cr: '28', proficiency: 8, ac: 19, hpMin: 716, hpMax: 760, attack: 13, dprMin: 267, dprMax: 284, saveDc: 22, numeric: 28, xp: 120000 },
  { cr: '29', proficiency: 9, ac: 19, hpMin: 761, hpMax: 805, attack: 13, dprMin: 285, dprMax: 302, saveDc: 22, numeric: 29, xp: 135000 },
  { cr: '30', proficiency: 9, ac: 19, hpMin: 806, hpMax: 850, attack: 14, dprMin: 303, dprMax: 320, saveDc: 23, numeric: 30, xp: 155000 },
]

// Add index to each CR entry
CR_DATA.forEach((cr, i) => { cr.index = i })

// CR select options for dropdowns
export const CR_SELECT = CR_DATA.map(cr => ({ label: cr.cr, value: cr.cr }))

// CR utility functions
export function getCrByString(crString) {
  return CR_DATA.find(cr => cr.cr === crString)
}

function getCrByRange(value, minField, maxField) {
  if (value <= CR_DATA[0][maxField]) return CR_DATA[0]
  if (value >= CR_DATA[CR_DATA.length - 1][minField]) return CR_DATA[CR_DATA.length - 1]

  for (const cr of CR_DATA) {
    if (value >= cr[minField] && value <= cr[maxField]) {
      return cr
    }
  }
  return CR_DATA[0]
}

export function getCrByHp(hp) {
  return getCrByRange(hp, 'hpMin', 'hpMax')
}

export function getCrByDamage(damage) {
  return getCrByRange(damage, 'dprMin', 'dprMax')
}

export function getCrByAc(ac) {
  // AC doesn't have a range, find closest
  let closest = CR_DATA[0]
  for (const cr of CR_DATA) {
    if (cr.ac <= ac) closest = cr
  }
  return closest
}

export function getCrByAttack(attack) {
  let closest = CR_DATA[0]
  for (const cr of CR_DATA) {
    if (cr.attack <= attack) closest = cr
  }
  return closest
}

export function getCrByDc(dc) {
  let closest = CR_DATA[0]
  for (const cr of CR_DATA) {
    if (cr.saveDc <= dc) closest = cr
  }
  return closest
}

// Calculate ability score modifier
export function getModifier(score) {
  return Math.floor((score - 10) / 2)
}

// Format modifier with sign
export function formatModifier(mod) {
  return mod >= 0 ? `+${mod}` : `${mod}`
}

// Calculate average dice roll
export function avgRoll(count, die) {
  return Math.floor(count * ((die + 1) / 2))
}

// Calculate proficiency bonus by CR
export function getProficiencyByCr(crString) {
  const cr = getCrByString(crString)
  return cr ? cr.proficiency : 2
}
